import React from 'react';

function useTimeoutRef() {
  const timeoutIdRef = React.useRef<ReturnType<typeof setTimeout>>();

  const resetTimeout = React.useCallback(() => {
    clearTimeout(timeoutIdRef.current);
  }, []);

  React.useEffect(() => {
    return () => {
      resetTimeout();
    };
  }, [resetTimeout]);

  return timeoutIdRef;
}

function useDebounce<ValueType = string>(
  value: ValueType,
  delayMs = 1000,
): [ValueType, (value: ValueType) => void] {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delayMs]);

  return [debouncedValue, setDebouncedValue];
}

function usePrevious<T>(value: T) {
  const ref = React.useRef<T>();

  React.useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

function useCompare<T>(val: T): boolean {
  const prevVal = usePrevious<T>(val);
  return prevVal !== val;
}

const useLatest = <T>(current: T) => {
  const storedValue = React.useRef(current);

  React.useEffect(() => {
    storedValue.current = current;
  });

  return storedValue;
};

export {useTimeoutRef, useDebounce, usePrevious, useCompare, useLatest};
